import { liveQuery } from 'dexie';

import { db, isDexieEnabled, AnalyticsData, Holdings, ExchangeKey, Snapshots } from './';

// No-op unsubscribe returned when the cache is disabled.
const noopUnsubscribe = () => { };

/**
 * Wrapper function to safely access the database instance
 * Ensures IndexedDB is supported
 */
function getDatabaseSafe() {
    return db;
}

export class AnalyticsQueries {
    /**
     * Get the latest analytics entry (live)
     * @param onUpdate - Callback function to handle live updates
     * @returns Function to unsubscribe from updates
     */
    static getLatestAnalytics(onUpdate: (data: AnalyticsData | undefined) => void): () => void {
        if (!isDexieEnabled()) return noopUnsubscribe;
        try {
            const database = getDatabaseSafe();
            const subscription = liveQuery(() =>
                database.analytics.orderBy('id').reverse().first()
            ).subscribe({
                next: (data) => onUpdate(data),
                error: (err) => console.error('Error in live analytics query:', err),
            });

            return () => subscription.unsubscribe();
        } catch (error) {
            console.error('Error initializing live analytics query:', error);
            return () => { }; // No-op unsubscribe function
        }
    }
}

export class HoldingsQueries {
    /**
     * Get live holdings updates
     * @param onUpdate - Callback function to handle live updates
     * @returns Function to unsubscribe from updates
     */
    static getHoldings(onUpdate: (data: Holdings[] | undefined) => void): () => void {
        if (!isDexieEnabled()) return noopUnsubscribe;
        try {
            const database = getDatabaseSafe();
            const subscription = liveQuery(() =>
                database.holdings.toArray()
            ).subscribe({
                next: (data) => onUpdate(data),
                error: (err) => console.error('Error in live holdings query:', err),
            });

            return () => subscription.unsubscribe();
        } catch (error) {
            console.error('Error initializing live holdings query:', error);
            return () => { }; // No-op unsubscribe function
        }
    }
}

export class ExchangeKeyQueries {
    /**
     * Get live updates for both exchange keys and selected exchange
     * @param onUpdate - Callback function to handle live updates with both datasets
     * @returns Function to unsubscribe from all updates
     */
    static getExchangeKeys(
        onUpdate: (data: {
            exchangeKeys: ExchangeKey[] | undefined;
            selectedExchange: ExchangeKey[] | undefined;
        }) => void
    ): () => void {
        if (!isDexieEnabled()) return noopUnsubscribe;
        try {
            const database = getDatabaseSafe();

            const subscription = liveQuery(() =>
                Promise.all([
                    database.exchangeKeys.toArray(),
                    database.selectedExchange.toArray()
                ])
            ).subscribe({
                next: ([exchangeKeys, selectedExchange]) =>
                    onUpdate({ exchangeKeys, selectedExchange }),
                error: (err) => console.error('Error in live combined query:', err),
            });

            return () => subscription.unsubscribe();
        } catch (error) {
            console.error('Error initializing live combined query:', error);
            return () => { }; // No-op unsubscribe function
        }
    }
}

export class SnapshotsQueries {
    /**
     * Get live snapshots data updates
     * @param onUpdate - Callback function to handle live updates
     * @param range - Range of data to retrieve (default: '1week')
     * @param exchangeKeyId - Exchange key ID (default: null)
     * @returns Function to unsubscribe from updates
     */
    static getEvaluationData(
        onUpdate: (data: Snapshots | undefined) => void,
        range: string = '1week',
        exchangeKeyId: number | null
    ): () => void {
        if (!isDexieEnabled()) return noopUnsubscribe;
        try {
            const database = getDatabaseSafe();
            const key = exchangeKeyId ?? 0;

            const subscription = liveQuery(() =>
                database.snapshots.where('[range+exchange_key_id]').equals([range, key]).first()
            ).subscribe({
                next: (data) => onUpdate(data),
                error: (err) => console.error('Error in live evaluation data query:', err),
            });

            return () => subscription.unsubscribe();
        } catch (error) {
            console.error('Error initializing live snapshots query:', error);
            return () => { }; // No-op unsubscribe function
        }
    }
}
